import { SettingCardViewAction, Xero } from '@monetize/types/app';
import { ReactNode, useState } from 'react';
import {
  useGetXeroCoa,
  useGetXeroCompanies,
  useGetXeroUserInfo,
  useUpdateAccountingConnectionStatus,
} from '../../../../../api/accountingService';
import { SettingCardEdit } from '../../../../../components/Settings/SettingCardEdit';
import { SettingCardView } from '../../../../../components/Settings/SettingCardView';
import { useToast } from '../../../../../services/toast';
import { XeroForm } from './XeroForm';
import { XERO_FORM_FIELDS } from './accounting.constants';

interface XeroProps {
  connection: Xero;
  description?: ReactNode;
}

export const XeroConnection = ({ connection, description }: XeroProps) => {
  const { addToast } = useToast();
  const isEnabled = connection.enabled;

  const { data: xeroUserInfo, isFetching: userInfoFetching } =
    useGetXeroUserInfo();
  const {
    data: xeroCoa,
    isFetching: coaListFetching,
    refetch: refetchCoa,
  } = useGetXeroCoa();
  const { data: xeroCompanies, isFetching: companiesFetching } =
    useGetXeroCompanies();
  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');

  const { mutate: updateConnection, isPending: isProcessing } =
    useUpdateAccountingConnectionStatus({
      meta: { showErrorToast: true },
    });

  const handleRefetchCoa = async () => {
    const result = await refetchCoa();

    if (result.isError) {
      addToast({
        summary: 'Error',
        detail: 'Failed to refresh COA data. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleAction = (action: SettingCardViewAction) => {
    if (isProcessing) {
      return;
    }
    switch (action) {
      case 'ENABLE':
        updateConnection({ type: 'xero', action: 'enable' });
        break;
      case 'DISABLE':
        updateConnection({ type: 'xero', action: 'disable' });
        break;
      default:
        break;
    }
  };

  return (
    <>
      {mode === 'VIEW' && (
        <SettingCardView
          status={isEnabled ? 'ACTIVE' : 'INACTIVE'}
          type="Xero"
          isLoading={
            isProcessing ||
            userInfoFetching ||
            coaListFetching ||
            companiesFetching
          }
          isEnabled={isEnabled}
          actions={[
            {
              action: isEnabled ? 'DISABLE' : 'ENABLE',
              label: isEnabled ? 'Disable' : 'Enable',
              isDanger: isEnabled,
              ...(!isEnabled
                ? {}
                : {
                    confirmProps: {
                      title:
                        'Are you sure you want to disable this connection?',
                      description:
                        'This will temporarily deactivate the Xero integration. You can re-enable it at any time.',
                    },
                  }),
            },
          ]}
          tags={[
            {
              label: XERO_FORM_FIELDS.companyId.label,
              tooltip: XERO_FORM_FIELDS.companyId.tooltip,
              value: connection.companyId?.value || 'Not Selected',
            },
            {
              label: XERO_FORM_FIELDS.defaultPaymentCoa.label,
              tooltip: XERO_FORM_FIELDS.defaultPaymentCoa.tooltip,
              value: connection?.defaultPaymentCoa
                ? `${connection.defaultPaymentCoa.code} - ${connection.defaultPaymentCoa.name}`
                : 'Blank',
              colSpan: 1,
            },
            {
              label: XERO_FORM_FIELDS.defaultProductCoa.label,
              tooltip: XERO_FORM_FIELDS.defaultProductCoa.tooltip,
              value: connection?.defaultProductCoa
                ? `${connection.defaultProductCoa.code} - ${connection.defaultProductCoa.name}`
                : 'Blank',
              colSpan: 1,
            },
            {
              label: XERO_FORM_FIELDS.name.label,
              value: xeroUserInfo?.name ?? 'Blank',
              colSpan: 1,
            },
            {
              label: XERO_FORM_FIELDS.email.label,
              value: xeroUserInfo?.email ?? 'Blank',
              colSpan: 1,
            },
            {
              label: XERO_FORM_FIELDS.userName.label,
              value: xeroUserInfo?.username ?? 'Blank',
              colSpan: 1,
            },
            {
              label: XERO_FORM_FIELDS.userId.label,
              value: xeroUserInfo?.userId ?? 'Blank',
              colSpan: 1,
            },
          ]}
          onEdit={() => {
            setMode('EDIT');
          }}
          onAction={handleAction}
        />
      )}
      {mode === 'EDIT' && (
        <SettingCardEdit
          showCloseButton
          type="Xero"
          handleCancel={() => setMode('VIEW')}
        >
          <XeroForm
            connection={connection}
            userInfo={xeroUserInfo}
            coaData={xeroCoa}
            onClose={() => setMode('VIEW')}
            refetchCoaData={handleRefetchCoa}
            isRefetchingCoa={coaListFetching}
            companiesData={xeroCompanies}
            isLoadingCompanies={companiesFetching}
          />
        </SettingCardEdit>
      )}
    </>
  );
};
